/**
 * Universal exception reporter. Safe to import from client OR server code.
 *
 * Behaviour:
 *  - Logs structured `[exception] {filename}[:{line}] in {method} — {msg}` to
 *    the console.
 *  - In the browser, routes the error to whichever reporter is registered:
 *      1. The AppSignal browser SDK, if `AppSignalBoot` registered one (see
 *         `registerBrowserReporter` below). Preferred — it carries
 *         breadcrumbs (network + console) that the server forwarder cannot
 *         see, and reports into the `frontend` namespace.
 *      2. Otherwise a fire-and-forget POST to `/api/exceptions`, which
 *         forwards through the server-side @appsignal/nodejs and adds the
 *         cookie-verified user id.
 *    Exactly one of the two runs per error — never both, or every client
 *    error would produce two incidents.
 *  - Drops 4xx errors (HTTP-shaped) before either logging or forwarding to
 *    keep AppSignal signal clean. The allowlist is empty by default.
 *  - Sets a dedupe marker on the error so a server-side forwarder doesn't
 *    re-report the same instance.
 *
 * It does NOT statically import @appsignal/nodejs (Node-only) OR
 * @appsignal/javascript (browser-only). Both would end up in the wrong
 * bundle. Server-side reporting goes through `exception-handler-context.ts`;
 * browser-side reporting arrives via the registration seam below, which the
 * client-only `AppSignalBoot` component fills in at mount.
 */
import { redactSearch, redactUrl, scrubText } from './appsignal-redaction';

import { recordClientError } from './client-error-buffer';
import { getOrCreateSessionId, getPageLoadTraceId } from './session-id';

export interface ExceptionContext {
  filename: string;
  line?: number;
  method?: string;
  tags?: Record<string, string | number | boolean | undefined>;
  /**
   * Structured input that plausibly caused the error. Mirrors the field on
   * the api's ExceptionContext so both apps share one reporting shape.
   *
   * Reaches AppSignal on every path: the browser SDK sets it via
   * `span.setParams`, the server forwarder via `setParams`.
   *
   * Scrubbing differs per path, so do not assume one covers the other:
   *  - Server forwarder → the agent applies `filterParameters` from
   *    appsignal.cjs, matching exact TOP-LEVEL key names.
   *  - Browser SDK → there is NO agent in the path. `redactParams` in
   *    appsignal-redaction.ts is the only scrub, and it is ours to maintain.
   *
   * Practical rule: pass real wire key names, and when you add a sensitive
   * one, add it to BOTH `filterParameters` (appsignal.cjs) and
   * `SENSITIVE_KEYS` (appsignal-redaction.ts) in the same change.
   */
  params?: Record<string, unknown>;
  /**
   * Where the error originated: `'browser'` or `'server'` (the default).
   *
   * A dedicated field rather than a tag because `source` is a TRUSTED tag —
   * it's in RESERVED_TAGS, so a caller-supplied `tags.source` is stripped.
   * /api/exceptions hardcodes `'browser'` here, which is what stops an
   * unauthenticated POST from relabelling an incident's origin.
   */
  source?: 'browser' | 'server';
}

/**
 * What the browser knows about itself at the moment of the error. Sent as
 * tags (so it is filterable in the AppSignal UI) rather than params.
 */
export interface BrowserContext {
  url?: string;
  pathname?: string;
  search?: string;
  referrer?: string;
  userAgent?: string;
  language?: string;
  viewport?: string;
  devicePixelRatio?: number;
  connectionEffectiveType?: string;
}

/**
 * SSR-safe: returns undefined on the server. This module is imported by
 * server code (see the header), so every DOM access must sit behind the
 * `typeof window` guard.
 */
export function browserContext(): BrowserContext | undefined {
  if (typeof window === 'undefined') return undefined;
  // `connection` is not in lib.dom.d.ts — narrow rather than reaching for any.
  const nav = window.navigator as Navigator & {
    connection?: { effectiveType?: string };
  };
  return {
    // url/search/referrer are REDACTED, not raw. Several routes carry secrets
    // in the query string (/reset-password?token_hash=…, ?email=…,
    // ?guest_email=…), and the App Router error boundaries report on any
    // render error on any route — so the raw href would ship live tokens to
    // AppSignal. Query values are stripped; key names survive for triage.
    // See appsignal-redaction.ts. Do not "simplify" these back to raw values.
    url: redactUrl(window.location.href),
    pathname: window.location.pathname,
    search: redactSearch(window.location.search),
    referrer: redactUrl(document.referrer) || undefined,
    userAgent: nav.userAgent,
    language: nav.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio,
    connectionEffectiveType: nav.connection?.effectiveType,
  };
}

/**
 * Registration seam for the AppSignal browser SDK.
 *
 * `AppSignalBoot` (a client component) calls this at mount. Keeping it a
 * runtime registration rather than a static import means this module stays
 * importable from server code without dragging @appsignal/javascript into
 * the server bundle.
 *
 * Returns true if the reporter accepted the error. A false return (or a
 * throw) falls back to the server forwarder, so a broken SDK degrades to the
 * previous behaviour instead of losing the report.
 */
export type BrowserReporter = (
  err: Error,
  context: ExceptionContext,
  browser: BrowserContext | undefined,
) => boolean;

let browserReporter: BrowserReporter | null = null;

export function registerBrowserReporter(fn: BrowserReporter | null): void {
  browserReporter = fn;
}

const REPORT_4XX_ALLOWLIST: ReadonlySet<string> = new Set<string>();

interface MaybeStatusError extends Error {
  status?: number;
  statusCode?: number;
  cause?: { status?: number };
}

function getStatus(err: MaybeStatusError): number | undefined {
  return err.status ?? err.statusCode ?? err.cause?.status;
}

export interface NormalizedReport {
  err: MaybeStatusError;
  where: string;
  /** True if the error should NOT be forwarded (4xx noise or already reported). */
  skip: boolean;
}

/**
 * Shared normalisation used by both the universal reporter (here) and the
 * server-only forwarder in exception-handler-context.ts. Centralising this
 * keeps the noise filter and dedupe rules in one place.
 */
export function prepareReport(
  error: unknown,
  context: ExceptionContext,
): NormalizedReport {
  const err =
    error instanceof Error
      ? (error as MaybeStatusError)
      : (new Error(String(error)) as MaybeStatusError);

  const status = getStatus(err);
  const noisy =
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    !REPORT_4XX_ALLOWLIST.has(err.constructor.name);

  const alreadyReported = (err as { __reportedToAppSignal?: boolean })
    .__reportedToAppSignal === true;

  if (!noisy && !alreadyReported) {
    try {
      (err as { __reportedToAppSignal?: boolean }).__reportedToAppSignal = true;
    } catch {
      // Frozen Error — accept tiny duplicate-report risk over crashing.
    }
  }

  const where =
    `${context.filename}` +
    (context.line ? `:${context.line}` : '') +
    (context.method ? ` in ${context.method}` : '');

  return { err, where, skip: noisy || alreadyReported };
}

export function report(error: unknown, context: ExceptionContext): void {
  const { err, where, skip } = prepareReport(error, context);
  if (skip) return;
  // eslint-disable-next-line no-console
  console.error(`[exception] ${where} — ${err.message}`, err);

  // No-op on the server — server-side code paths use reportWithContext.
  if (typeof window === 'undefined') return;

  // Feed the bug-report error buffer FIRST, before either transport. A
  // subsequent "Report a problem" submission must carry the errors that
  // preceded it whether or not the browser SDK is booted — and the SDK path
  // returns early below, so buffering after it would silently drop every
  // error on any environment that has a front-end key configured.
  recordClientError(err, context.filename);

  const browser = browserContext();

  // Correlation keys are attached HERE, once, rather than inside either
  // transport. The two paths are mutually exclusive — the SDK returns early —
  // so tagging inside only one of them silently drops sessionId/traceId on
  // whichever environments have a front-end key configured. Those are the keys
  // triage uses to match a support ticket to the incidents its browser session
  // produced, and losing them would not fail any build.
  const correlated: ExceptionContext = {
    ...context,
    tags: {
      ...context.tags,
      sessionId: getOrCreateSessionId(),
      traceId: getPageLoadTraceId(),
    },
  };

  // Prefer the browser SDK when it is booted: it attaches breadcrumbs and
  // reports into the `frontend` namespace. Fall back to the server forwarder
  // when it is absent (no front-end key configured) or when it declines.
  if (browserReporter) {
    try {
      if (browserReporter(err, correlated, browser)) return;
    } catch {
      // A broken SDK must not swallow the report — fall through to the POST.
    }
  }

  void forwardToServer(err, correlated, browser);
}

function forwardToServer(
  err: MaybeStatusError,
  context: ExceptionContext,
  browser: BrowserContext | undefined,
): void {
  // Fire-and-forget. Never block the UI on telemetry, never let a forwarder
  // failure surface as another error to the user. If /api/exceptions itself
  // is broken, the console.error above is still the diagnostic of last resort.
  try {
    const body = JSON.stringify({
      // Scrubbed: nothing else in the pipeline touches the error's own message
      // or stack, so a message built as `Failed to fetch ${url}` on a page
      // carrying ?token_hash= would leak despite every tag being redacted.
      // No current call site does that — this closes the vector rather than
      // fixing a live leak.
      message: scrubText(err.message),
      name: err.name,
      stack: err.stack ? scrubText(err.stack) : undefined,
      filename: context.filename,
      line: context.line,
      method: context.method,
      // sessionId/traceId are already merged into context.tags by report()
      // so both transports carry them — see the comment there.
      tags: context.tags,
      params: context.params,
      browser,
    });
    void fetch('/api/exceptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // Keep the request alive across navigation so error.tsx's reset()
      // doesn't drop telemetry on the unmount.
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      // Swallow — see the policy above.
    });
  } catch {
    // JSON.stringify failure (e.g. circular ref on a weird error) — drop.
  }
}

export const exceptionHandler = { report };
